import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import axios from "axios";
import { Button, Checkbox, InputNumber, Layout, Popover, Select, Space, Table, Typography } from "antd";
import { formatNumber } from "../../../util/NumberFormatter";

dayjs.extend(customParseFormat);
dayjs.locale('ko');

const tierOrder = ['Normal', 'Iron', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Meteorite', 'Mithril'];
const tierDisplayMap = {
  'Mithril': '미스릴+',
  'Meteorite': '메테오라이트+',
  'Diamond': '다이아몬드+',
  'Platinum': '플래티넘+',
  'Gold': '골드+',
  'Silver': '실버+',
  'Bronze': '브론즈+',
  'Iron': '아이언+',
  'Normal': '전체(일반포함)'
};
const tierDropdownOptions = tierOrder.slice().reverse().map(tier => {
  const imagePath = `/img/tier/${tier}.png`;
  return {
    value: tier,
    label: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img
          src={imagePath}
          alt={tier}
          style={{
            height: '20px',
            width: 'auto',
            marginRight: '8px'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <span>{tierDisplayMap[tier]}</span>
      </div>
    ),
  };
});

const getOutlierStyle = (
  currentValue,
  average,
  lowerIsBetter = false,
  thresholdPercent = 0.15,
  goodColor = 'red',
  badColor = '#07ae04'
) => {
  let style = {};
  if (average === null || average === undefined || isNaN(average) || average === 0 || isNaN(currentValue)) {
    return style;
  }

  const lowerBound = average * (1 - thresholdPercent);
  const upperBound = average * (1 + thresholdPercent);

  if (lowerIsBetter) {
    if (currentValue < lowerBound) {
      style.color = goodColor;
      style.fontWeight = 'bold';
    } else if (currentValue > upperBound) {
      style.color = badColor;
    }
  } else {
    if (currentValue > upperBound) {
      style.color = goodColor;
      style.fontWeight = 'bold';
    } else if (currentValue < lowerBound) {
      style.color = badColor;
    }
  }
  return style;
};

export default function TopRanker() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState([]);
  const [totalGameCount, setTotalGameCount] = useState(0);
  const [selectedMinTier, setSelectedMinTier] = useState('Diamond');
  const [mainWeathers, setMainWeathers] = useState({ '흐림': true, '쾌청': true, '비': true, '모래바람': true });
  const [subWeathers, setSubWeathers] = useState({ '무풍': true, '안개': true, '강풍': true, '벼락': true });
  const [daysAgo, setDaysAgo] = useState(3);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const handleMinTierChange = (value) => setSelectedMinTier(value);
  const handleMainWeatherChange = (mainWeather) => setMainWeathers(prev => ({ ...prev, [mainWeather]: !prev[mainWeather] }));
  const handleSubWeatherChange = (subWeather) => setSubWeathers(prev => ({ ...prev, [subWeather]: !prev[subWeather] }));

  const handleDaysChange = (newDays) => {
    const validatedDays = (typeof newDays === 'number' && newDays > 0) ? newDays : 3;
    setDaysAgo(validatedDays);
  };

  const handlePresetClick = (presetDays) => {
    handleDaysChange(presetDays);
    setPopoverVisible(false);
  };

  const popoverContent = (
    <Space direction="vertical" style={{ width: '250px' }}>
      <InputNumber
        min={1}
        max={31}
        value={daysAgo}
        onChange={(value) => {
          handleDaysChange(value === null ? 1 : value);
        }}
        addonAfter="일 전"
        style={{ width: '100%' }}
      />
      <Space wrap>
        <Button onClick={() => handlePresetClick(1)}>하루 전</Button>
        <Button onClick={() => handlePresetClick(3)}>3일 전</Button>
        <Button onClick={() => handlePresetClick(7)}>1주 전</Button>
        <Button onClick={() => handlePresetClick(14)}>2주 전</Button>
        <Button onClick={() => handlePresetClick(31)}>한 달 전</Button>
      </Space>
    </Space>
  );

  useEffect(() => {
    setLoading(true);
    let main = Object.keys(mainWeathers).filter(weatherKey => mainWeathers[weatherKey]).join(',')
    let sub = Object.keys(subWeathers).filter(weatherKey => subWeathers[weatherKey]).join(',')

    axios.get("/api/external/bser/top-ranker-stats", {
      params: {
        days: daysAgo,
        tier: selectedMinTier,
        mainWeathers: main,
        subWeathers: sub
      }
    })
      .then(r => r.data)
      .then(data => {
        const sortedStats = Object.values(data).sort((a, b) => b.score - a.score);
        setStats(sortedStats)
      })
      .finally(() => setLoading(false));
  }, [daysAgo, selectedMinTier, mainWeathers, subWeathers]);

  useEffect(() => {
    setTotalGameCount(stats.reduce((sum, stat) => sum + stat.gameCount, 0));
  }, [stats]);

  const averages = useMemo(() => {
    if (!stats || stats.length === 0) {
      return {
        avgRank: null, avgWinRate: null, avgTop3Rate: null,
        avgDamagePerGame: null, avgDps: null,
        avgKillsPerGame: null, avgMmrGain: null
      };
    }

    let sumRank = 0, countRank = 0;
    let sumWinRate = 0, countWinRate = 0;
    let sumTop3Rate = 0, countTop3Rate = 0;
    let sumDamagePerGame = 0, countDamagePerGame = 0;
    let sumDps = 0, countDps = 0;
    let sumKillsPerGame = 0, countKillsPerGame = 0;
    let sumMmrGainPerGame = 0, countMmrGainPerGame = 0;

    stats.forEach(stat => {
      if (stat.gameCount > 0) {
        sumRank += (stat.gameRank / stat.gameCount);
        countRank++;

        sumWinRate += (stat.victory / stat.gameCount);
        countWinRate++;

        sumTop3Rate += (stat.top3 / stat.gameCount);
        countTop3Rate++;

        sumDamagePerGame += (stat.damageToPlayer / stat.gameCount);
        countDamagePerGame++;

        if (stat.totalTime > 0) {
          sumDps += (stat.damageToPlayer / stat.totalTime);
          countDps++;
        }

        sumKillsPerGame += (stat.playerKill / stat.gameCount);
        countKillsPerGame++;

        sumMmrGainPerGame += (stat.mmrGain / stat.gameCount);
        countMmrGainPerGame++;
      }
    });

    return {
      avgRank: countRank > 0 ? sumRank / countRank : null,
      avgWinRate: countWinRate > 0 ? sumWinRate / countWinRate : null,
      avgTop3Rate: countTop3Rate > 0 ? sumTop3Rate / countTop3Rate : null,
      avgDamagePerGame: countDamagePerGame > 0 ? sumDamagePerGame / countDamagePerGame : null,
      avgDps: countDps > 0 ? sumDps / countDps : null,
      avgKillsPerGame: countKillsPerGame > 0 ? sumKillsPerGame / countKillsPerGame : null,
      avgMmrGain: countMmrGainPerGame > 0 ? sumMmrGainPerGame / countMmrGainPerGame : null,
    };
  }, [stats]);

  const columns = [
    {
      title: '순위',
      align: 'center',
      render: (text, record, index) => <span style={{ textAlign: 'center' }}>{index + 1}</span>,
      fixed: 'left'
    },
    {
      title: '실험체',
      fixed: 'left',
      render: (stat) => {
        const characterImgSrc = `/img/character/${stat.characterName}.png`;
        const weaponImgSrc = `/img/weapon_skill/${stat.bestWeapon}.png`;

        const handleCharacterClick = () => {
          navigate(`/bser/character-stats?characterName=${encodeURIComponent(stat.characterName)}&bestWeapon=${encodeURIComponent(stat.bestWeapon)}`);
        };

        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              textAlign: 'left',
              cursor: 'pointer'
            }}
            onClick={handleCharacterClick}
          >
            <div style={{
              position: 'relative',
              width: '36px',
              height: '36px',
              marginRight: '8px',
              flexShrink: 0,
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: '#d6d6d6',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <img
                  src={characterImgSrc}
                  alt={stat.characterName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <img
                src={weaponImgSrc}
                alt={stat.bestWeapon}
                style={{
                  position: 'absolute',
                  bottom: '0px',
                  right: '-8px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: '#323232',
                  padding: '2px',
                  border: '1px solid #4A4A4A',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <span style={{ whiteSpace: 'nowrap', color: '#1890ff' }}>
              {stat.characterName} [{stat.bestWeapon}]
            </span>
          </div>
        );
      }
    },
    {
      title: '픽 수',
      align: 'center',
      dataIndex: 'gameCount',
      sorter: (a, b) => a.gameCount - b.gameCount,
      render: (gameCount) => (
        <span style={{ textAlign: 'center' }}>
          <div>
            {totalGameCount > 0 ? ((gameCount / totalGameCount) * 100).toFixed(2) : 0}%
          </div>
          <div style={{ fontSize: '0.77em' }}>
            ({formatNumber(gameCount)})
          </div>
        </span>
      )
    },
    {
      title: 'score',
      align: 'center',
      sorter: (a, b) => a.score - b.score,
      render: (stat) => {
        const src = `/img/character_tier/${stat.characterTier}.svg`

        return (<div style={{
            display: 'flex',
            justifyContent: 'center', // 가운데 정렬로 변경
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <div style={{
              position: 'relative',
              width: '36px',
              height: '36px',
              marginRight: '8px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center', // 이 부분 추가
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: '#ffffff',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {src && (
                  <img
                    src={src}
                    alt={stat.characterTier}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      title: 'RP 획득량',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.mmrGain / a.gameCount : Infinity) - (b.gameCount > 0 ? b.mmrGain / b.gameCount : Infinity),
      render: (stat) => {
        const currentValue = stat.mmrGain / stat.gameCount;
        const displayValue = currentValue.toFixed(2);
        const style = getOutlierStyle(currentValue, averages.avgMmrGain, false, 0.2);
        return <span style={{ ...style, textAlign: 'center' }}>{displayValue}</span>;
      }
    },
    {
      title: '평균 등수',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.gameRank / a.gameCount : Infinity) - (b.gameCount > 0 ? b.gameRank / b.gameCount : Infinity),
      render: (stat) => {
        if (stat.gameCount === 0) {
          return '-';
        }
        const currentValue = stat.gameRank / stat.gameCount;
        const displayValue = currentValue.toFixed(2);
        const style = getOutlierStyle(currentValue, averages.avgRank, true, 0.05);
        return <span style={{ ...style, textAlign: 'center' }}>{displayValue}</span>;
      }
    },
    {
      title: '승률',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.victory / a.gameCount : 0) - (b.gameCount > 0 ? b.victory / b.gameCount : 0),
      render: (stat) => {
        let percentageDisplay;
        let absoluteValueDisplay;
        let currentValueForStyle;

        if (stat.gameCount === 0) {
          percentageDisplay = "0.00%";
          absoluteValueDisplay = stat.victory;
          currentValueForStyle = 0;
        } else {
          const winRate = stat.victory / stat.gameCount;
          percentageDisplay = `${(winRate * 100).toFixed(2)}%`;
          absoluteValueDisplay = stat.victory;
          currentValueForStyle = winRate;
        }
        const style = getOutlierStyle(currentValueForStyle, averages.avgWinRate, false);

        return (
          <span style={{ ...style, textAlign: 'center' }}>
            <div>{percentageDisplay}</div>
        <div style={{ fontSize: '0.77em' }}>
          ({absoluteValueDisplay})
        </div>
      </span>
        );
      }
    },
    {
      title: 'Top 3',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.top3 / a.gameCount : 0) - (b.gameCount > 0 ? b.top3 / b.gameCount : 0),
      render: (stat) => {
        let percentageDisplay;
        let absoluteValueDisplay;
        let currentValueForStyle;

        if (stat.gameCount === 0) {
          percentageDisplay = "0.00%";
          absoluteValueDisplay = stat.top3;
          currentValueForStyle = 0;
        } else {
          const winRate = stat.top3 / stat.gameCount;
          percentageDisplay = `${(winRate * 100).toFixed(2)}%`;
          absoluteValueDisplay = stat.top3;
          currentValueForStyle = winRate;
        }
        const style = getOutlierStyle(currentValueForStyle, averages.avgTop3Rate, false);
        return (
          <span style={{ ...style, textAlign: 'center' }}>
            <div>{percentageDisplay}</div>
        <div style={{ fontSize: '0.77em' }}>
          ({absoluteValueDisplay})
        </div>
      </span>
        );
      }
    },
    {
      title: '딜량 [dps]',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.damageToPlayer / a.gameCount : 0) - (b.gameCount > 0 ? b.damageToPlayer / b.gameCount : 0),
      render: (stat) => {
        if (stat.gameCount === 0) {
          return '- [-]';
        }
        const damagePerGame = stat.damageToPlayer / stat.gameCount;
        const dps = stat.totalTime > 0 ? (stat.damageToPlayer / stat.totalTime) : 0;
        return <span style={{ textAlign: 'center' }}>{formatNumber(Math.floor(damagePerGame))} [{dps.toFixed(2)}]</span>;
      }
    },
    {
      title: '킬 수',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.playerKill / a.gameCount : 0) - (b.gameCount > 0 ? b.playerKill / b.gameCount : 0),
      render: (stat) => {
        if (stat.gameCount === 0) {
          return '-';
        }
        return <span style={{ textAlign: 'center' }}>{(stat.playerKill / stat.gameCount).toFixed(2)}</span>;
      }
    },
    {
      title: 'TK',
      align: 'center',
      sorter: (a, b) => (a.gameCount > 0 ? a.teamKill / a.gameCount : 0) - (b.gameCount > 0 ? b.teamKill / b.gameCount : 0),
      render: (stat) => {
        if (stat.gameCount === 0) {
          return '-';
        }
        return <span style={{ textAlign: 'center' }}>{(stat.teamKill / stat.gameCount).toFixed(2)}</span>;
      }
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
  };

  const weatherFilterContent = (
    <Space direction="vertical" size="middle">
      <div>
        <Typography.Text strong>주 날씨 : </Typography.Text>
        <div style={{ marginTop: 8 }}>
          <Space wrap>
            {Object.keys(mainWeathers).map(mainWeather => (
              <Checkbox
                key={mainWeather}
                checked={mainWeathers[mainWeather]}
                onChange={() => handleMainWeatherChange(mainWeather)}
              >
                {mainWeather}
              </Checkbox>
            ))}
          </Space>
        </div>
      </div>
      <div>
        <Typography.Text strong>보조 날씨 : </Typography.Text>
        <div style={{ marginTop: 8 }}>
          <Space wrap>
            {Object.keys(subWeathers).map(subWeather => (
              <Checkbox
                key={subWeather}
                checked={subWeathers[subWeather]}
                onChange={() => handleSubWeatherChange(subWeather)}
              >
                {subWeather}
              </Checkbox>
            ))}
          </Space>
        </div>
      </div>
    </Space>
  );

  return (
    <Layout style={{ background: '#ffffff' }}>
      <Layout.Content style={{ padding: '20px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '20px' }}>
            <Space direction="horizontal" align="center">
              <Popover
                content={popoverContent}
                title={<Typography.Text strong>기간 선택</Typography.Text>}
                trigger="click"
                open={popoverVisible}
                onOpenChange={setPopoverVisible}
              >
                <Button style={{ minWidth: '180px', textAlign: 'center' }}>
                  {`${daysAgo}일 전`}
                </Button>
              </Popover>
              <Select
                value={selectedMinTier}
                style={{ width: 180 }}
                onChange={handleMinTierChange}
                options={tierDropdownOptions}
                listHeight={288}
              />
              <Popover content={weatherFilterContent} title={<Typography.Text strong>날씨 필터 설정</Typography.Text>} trigger="click"
                       placement="bottom">
                <Button>날씨 필터</Button>
              </Popover>
            </Space>
            <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              총 픽 수: {formatNumber(totalGameCount)}
            </div>
          </div>
          <Table
            className='table-wrapper-with-border'
            pagination={false}
            dataSource={stats}
            columns={columns}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
            loading={loading}
            rowKey={(record) => `${record.characterName}-${record.bestWeapon}`}
          />
        </Space>
      </Layout.Content>
    </Layout>
  );
}
